import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Webhook, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

export function WebhookEventsViewer() {
  const qc = useQueryClient();
  const [source, setSource] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['webhook-events', source],
    queryFn: async () => {
      let q = supabase.from('webhook_events' as any).select('*').order('created_at', { ascending: false }).limit(100);
      if (source) q = q.eq('source', source);
      const { data, error } = await q;
      if (error) throw error;
      return (data as any[]) || [];
    },
  });

  const sources = Array.from(new Set((data || []).map((r) => r.source)));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Webhook className="h-5 w-5 text-primary" /> Webhook Events
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => qc.invalidateQueries({ queryKey: ['webhook-events'] })}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-3">
          <Button size="sm" variant={!source ? 'default' : 'outline'} onClick={() => setSource('')}>All</Button>
          {['chapa', 'telebirr', 'marketing', ...sources].filter((v, i, a) => a.indexOf(v) === i).map((s) => (
            <Button key={s} size="sm" variant={source === s ? 'default' : 'outline'} onClick={() => setSource(s)}>{s}</Button>
          ))}
        </div>
        {isLoading ? <Skeleton className="h-40" /> : (
          <Table>
            <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Source</TableHead><TableHead>Event</TableHead><TableHead>Status</TableHead><TableHead>Sig</TableHead><TableHead>External ID</TableHead><TableHead>Error</TableHead></TableRow></TableHeader>
            <TableBody>
              {(data || []).map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="text-xs">{format(new Date(e.created_at), 'd MMM HH:mm:ss')}</TableCell>
                  <TableCell><Badge variant="outline">{e.source}</Badge></TableCell>
                  <TableCell className="text-xs">{e.event_type}</TableCell>
                  <TableCell>
                    <Badge variant={e.status === 'processed' ? 'default' : e.status === 'failed' ? 'destructive' : 'secondary'}>{e.status}</Badge>
                  </TableCell>
                  <TableCell>{e.signature_valid ? '✅' : '⚠️'}</TableCell>
                  <TableCell className="text-xs font-mono max-w-[180px] truncate">{e.external_id || '—'}</TableCell>
                  <TableCell className="text-xs text-destructive max-w-[200px] truncate">{e.error || '—'}</TableCell>
                </TableRow>
              ))}
              {!data?.length && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No webhook events yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
